import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState, DateCellEditor } from "components";
import { normalizeDateInput } from "components/ui-common/AgGridTable/DateCellEditor";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { useEmployee, useEmployeeStore } from "hooks";
import { Employee, EmployeeForm, EmployeeQuery } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { Plus } from "lucide-react";

const genderOptions = [
  { value: "male", label: "Nam" },
  { value: "female", label: "Nữ" },
  { value: "other", label: "Khác" },
];

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const parseBoolean = (value: unknown, defaultValue = true) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return false;
  }
  return defaultValue;
};

const defaultFormValues: EmployeeForm = {
  name: "",
  email: "",
  phone: "",
  birthday: "",
  gender: "",
  department_id: undefined,
  position: "",
  address: "",
  stop_follow: true,
};

const EmployeeObjective: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Employee | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<EmployeeQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<Employee | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useEmployeeStore();
  const { fetchList, createEmployee, updateEmployee, deleteEmployee } =
    useEmployee();

  const { data: employeeData, isFetching, refetch } = fetchList(queryParams, true);

  useEffect(() => {
    if (employeeData?.data) {
      setRows(employeeData.data as Employee[]);
    }
  }, [employeeData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createEmployee.isPending ||
        updateEmployee.isPending ||
        deleteEmployee.isPending,
      ),
    );
  }, [
    isFetching,
    createEmployee.isPending,
    updateEmployee.isPending,
    deleteEmployee.isPending,
    setLoading,
  ]);

  const handleDeleteRow = useCallback((row: Employee) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteEmployee.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa nhân viên không thành công");
      },
    });
  }, [confirmRow, deleteEmployee, editingId, removeRow, setEditingId]);

  const columns = useMemo<ColDef<Employee>[]>(() => {
    return [
      {
        headerName: "Tên nhân viên",
        field: "name",
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 180,
      },
      {
        headerName: "Số điện thoại",
        field: "phone",
        minWidth: 150,
      },
      {
        headerName: "Ngày sinh",
        field: "birthday",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: compareDate,
        },
        valueFormatter: (params) => formatDate(params.value),
        // cellEditor: DateCellEditor,
        valueParser: (params) => normalizeDateInput(params.newValue),
      },
      {
        headerName: "Giới tính",
        field: "gender",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: genderOptions.map((g) => g.value),
        },
        valueFormatter: (params) =>
          genderOptions.find((item) => item.value === params.value)?.label ||
          params.value,
      },
      {
        headerName: "Phòng ban ID",
        field: "department_id",
        minWidth: 140,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Chức vụ",
        field: "position",
        minWidth: 160,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: "Trạng thái",
        field: "stop_follow",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["true", "false"],
        },
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["true", "false"],
        },
        valueFormatter: (params) =>
          params.value === true || params.value === "true"
            ? "Hoạt động"
            : "Ngưng",
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<Employee>): EmployeeForm => ({
      name: String(row.name ?? "").trim(),
      email: String(row.email ?? "").trim(),
      phone: String(row.phone ?? "").trim(),
      birthday: String(row.birthday ?? "").trim(),
      gender: String(row.gender ?? "").trim(),
      department_id:
        row.department_id === undefined || row.department_id === null
          ? undefined
          : Number(row.department_id),
      position: String(row.position ?? "").trim(),
      address: String(row.address ?? "").trim(),
      stop_follow: parseBoolean(row.stop_follow),
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      name: "",
      email: "",
      phone: "",
      birthday: "",
      gender: "",
      department_id: undefined,
      position: "",
      address: "",
      stop_follow: true,
    } as Employee;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Employee>, meta?: PersistMeta) => {
      console.log(row,'row')
      const payload = normalizePayload(row);

      if (!payload.name) {
        SnackbarUtils.error("Tên nhân viên là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createEmployee.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Employee);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm nhân viên không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được nhân viên cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateEmployee.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Employee);
            } else {
              upsertRow({ ...(row as Employee), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật nhân viên không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createEmployee,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateEmployee,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: Employee, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow('edit')
      setEditingId(params.data.id);
      // reset({
      //   code: params.data.code,
      //   name: params.data.name,
      //   type: params.data.type,
      //   description: params.data.description || "",
      // });

      setTimeout(() => gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "name",
      }), 300);
    },
    [setEditingId],
  );

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Nhân viên</h1>
        <p className="text-sm text-gray-600">
          Quản lý nhân viên: thêm mới, chỉnh sửa trực tiếp và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách nhân viên
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={employeeData?.totalPage}
          onDelete={handleDeleteRow}
          onEdit={handleEditRow}
          onSave={handleSaveRow}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onAfterActions={() => {
            setShowInlineAddRow('');
            setEditingId(null)
          }}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách nhân viên, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa nhân viên?"
        description={`Bạn chắc chắn muốn xóa nhân viên "${confirmRow?.name ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteEmployee.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default EmployeeObjective;
